import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';

import FormFieldError from './FormFieldError';
import FormFieldLabel from './FormFieldLabel';
import FormFieldTooltip, { TooltipSource } from './FormFieldTooltip';
import useFormField, { FieldEvent, FieldWidth } from './useFormField';

interface IProps {
  name: string;
  label?: string;
  tooltip?: TooltipSource;
  description?: string;
  placeholder?: string;
  disabled?: boolean;
  width?: FieldWidth;
  rows?: number;
  autoResize?: boolean;
  autoFocus?: boolean;
  onFocus?: FieldEvent<string>;
  onChange?: FieldEvent<string>;
  onBlur?: FieldEvent<string>;
}

function FormTextAreaField({
  name,
  label,
  tooltip,
  description,
  placeholder,
  disabled,
  width,
  onFocus,
  onChange,
  onBlur,
  rows = 4,
  autoResize = false,
  autoFocus = false,
}: IProps) {
  const ref = useRef<HTMLTextAreaElement>();
  const adjustmentRef = useRef<number>(0);
  const {
    id,
    fieldProps,
    inputProps,
    required,
    disabled: formDisabled,
  } = useFormField<string>(name, width, { onFocus, onChange, onBlur }, autoFocus);

  useEffect(() => {
    if (autoResize) {
      adjustmentRef.current = Math.max(
        ref.current.getBoundingClientRect().height - ref.current.scrollHeight,
        0,
      );
      onInput();
    }
  }, []);

  const onInput = () => {
    ref.current.style.height = 'auto';
    ref.current.style.height = `${ref.current.scrollHeight + adjustmentRef.current}px`;
  };

  const renderRawValue = (value: string) => {
    const lines = value.split('\n');

    return lines.map((line, i) => (
      <React.Fragment key={i}>
        {line}
        {i < lines.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  const value = inputProps.value || '';
  const className = classNames('novoFormField__input novoFormField__input--text-area', {
    'novoFormField__input--auto-resize': autoResize,
  });
  return (
    <div {...fieldProps}>
      {label && (
        <div className="novoFormField__name">
          <FormFieldLabel for={id} required={required}>
            {label}
          </FormFieldLabel>
          <FormFieldTooltip source={tooltip} />
        </div>
      )}
      {description && <p className="novoFormField__description">{description}</p>}
      <textarea
        ref={ref}
        className={className}
        id={id}
        placeholder={placeholder}
        rows={autoResize ? 1 : rows}
        {...inputProps}
        onInput={autoResize ? onInput : undefined}
        required={required}
        disabled={formDisabled || disabled}
        value={value}
      />
      <p className="novoFormField__raw" aria-hidden="true">
        {renderRawValue(value)}
      </p>
      <FormFieldError name={name} />
    </div>
  );
}

export default FormTextAreaField;
